import { useEffect, useState } from 'react';
import { Box, Checkbox, Divider, FormControl, FormControlLabel, FormGroup, Grid, List, ListItem, Stack, TextField, Typography } from '@mui/material';

import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { formatDate } from '../../../../utils/dateHelpers';

import GuardianOrCaregiverInput from '../../Shared/GuardianOrCaregiverInput';
import Signature from '../../Shared/Signature';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';
import { RootState } from '../../../../reducers';
import { HealthPlansEnum } from '../../../../Enum/HealthPlansEnum';
import { setDigitalSignatureForm } from '../../../../store/consentSlice.slice';
import { ConsentSignatureTypeEnum } from '../../../../Enum/ConsentSignatureTypeEnum';
import SignatureTypeTabs from '../../Shared/SignatureTypeTabs';

export default function ReleaseHealthInfoSpanish() {
    const { activeStep, digitalSignatureForm } = useSelector((state: RootState) => state.consentSlice);
    const dispatch = useAppDispatch();
    const [openReleaseHealthInfoSignature, setOpenReleaseHealthInfoSignature] = useState(false);
    const [openGuardianOrCareGiverHealthInfo, setOpenGuardianOrCareGiverHealthInfo] = useState(false);
    const [openGuardianOrCareGiverWitnessHealthInfo, setOpenGuardianOrCareGiverWitnessHealthInfo] = useState(false);
    const [openVerbalConsent, setOpenVerbalConsent] = useState(false);

    const handleHealthPlanChange = (plan: HealthPlansEnum) => {
        const updatedHealthPlans = digitalSignatureForm.HealthPlans.includes(plan)
            ? digitalSignatureForm.HealthPlans.filter((p) => p !== plan)
            : [...digitalSignatureForm.HealthPlans, plan];

        dispatch(
            setDigitalSignatureForm({
                ...digitalSignatureForm,
                HealthPlans: updatedHealthPlans,
            })
        );
    };

    useEffect(() => {
        const element = document.getElementById('top');
        element.scrollIntoView({ behavior: 'smooth' });
    }, [activeStep]);

    return (
        <>
            <div id="top" className="initials-div"></div>
            <Stack textAlign={'left'} spacing={2} p={2} mt={5}>
                <Typography variant="h5" p={2} fontWeight={'bold'} textAlign={'center'}>
                    AUTORIZACIÓN PARA LA LIBERACIÓN DE INFORMACIÓN DE SALUD
                </Typography>
                <Typography variant="body1" fontWeight={800}>
                    YO, QUIEN FIRMA ABAJO, EXPRESAMENTE AUTORIZO LA LIBERACIÓN DE INFORMACIÓN PERSONAL Y DE SALUD AL INDIVIDUO/ENTIDAD NOMBRADA ABAJO PARA LOS
                    PROPÓSITOS ESPECIFICADOS EN ESTE DOCUMENTO:
                </Typography>
                <Typography variant="body1">
                    <b>Nombre: CATALYTIC HEALTH PARTNERS, INC.,</b> una corporación de Arizona
                </Typography>
                <Typography variant="body1">
                    <b>Relación:</b> entidad no-clínica de servicios de administración y manejo para <b>CHP CARE, P.C.,</b> una corporación profesional de
                    Arizona.
                </Typography>
                <Typography variant="body1">
                    <b>Dirección:</b> 3050 W Agua Fria Freeway, Suite 150, Phoenix, AZ 85027
                </Typography>
                <Typography variant="body1">
                    <b>Propósitos:</b> Coordinación de Cuidado
                </Typography>
                <Typography variant="body1">
                    Si yo opté por recibir confirmaciones de citas mediante mensajes de texto y/o correo electrónico (e-mail), para permitir que CHP Care, P.C.,
                    divulgue mi información de salud protegida (incluyendo mi nombre, información de contacto e información relacionada a tratamientos y
                    cuidados futuros) a Catalytic Health Partners, Inc. Para que se use en el procesamiento y enviado de dichas confirmaciones de citas a mi
                    persona mediante mensajes de texto y/o e-mail.
                </Typography>
                <Typography variant="body1">
                    Para la coordinación de la atención entre CHP Care y mi plan de salud y mis proveedores médicos y de salud del comportamiento, incluyendo
                    para los propósitos enlistados aquí:
                </Typography>

                <FormGroup sx={{ px: 2 }}>
                    <List sx={{ p: 2 }}>
                        <ListItem>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={digitalSignatureForm.CareCoordinationSubstanceDisorder}
                                        onChange={(e) =>
                                            dispatch(
                                                setDigitalSignatureForm({
                                                    ...digitalSignatureForm,
                                                    CareCoordinationSubstanceDisorder: e.target.checked,
                                                })
                                            )
                                        }
                                    />
                                }
                                label={
                                    <Typography variant="body1">
                                        La liberación de documentación perteneciente a mi tratamiento incluyendo desordenes de abuso de sustancias
                                    </Typography>
                                }
                            />
                        </ListItem>
                        <ListItem>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={digitalSignatureForm.CareCoordinationMentalHealth}
                                        onChange={(e) =>
                                            dispatch(
                                                setDigitalSignatureForm({
                                                    ...digitalSignatureForm,
                                                    CareCoordinationMentalHealth: e.target.checked,
                                                })
                                            )
                                        }
                                    />
                                }
                                label={
                                    <Typography variant="body1">
                                        La liberación de documentación relacionada con Salud del Comportamiento o tratamiento de salud mental
                                    </Typography>
                                }
                            />
                        </ListItem>
                        <ListItem>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={digitalSignatureForm.CareCoordinationRelatedIssues}
                                        onChange={(e) =>
                                            dispatch(
                                                setDigitalSignatureForm({
                                                    ...digitalSignatureForm,
                                                    CareCoordinationRelatedIssues: e.target.checked,
                                                })
                                            )
                                        }
                                    />
                                }
                                label={
                                    <Typography variant="body1">La liberación de documentación relacionada a otros problemas médicos o de salud.</Typography>
                                }
                            />
                        </ListItem>
                    </List>
                    <TextField
                        variant="standard"
                        value={digitalSignatureForm.ProviderName}
                        onChange={(e) =>
                            dispatch(
                                setDigitalSignatureForm({
                                    ...digitalSignatureForm,
                                    ProviderName: e.target.value,
                                })
                            )
                        }
                        label={`Nombre de Proveedor`}
                        sx={{ my: 2 }}
                        fullWidth
                    />

                    <Grid container spacing={2} my={2} pl={4}>
                        {Object.keys(HealthPlansEnum)
                            .filter((key) => isNaN(Number(key))) // Filter out numeric keys
                            .map((key) => {
                                const plan = HealthPlansEnum[key as keyof typeof HealthPlansEnum];
                                const description = HealthPlansEnum[`${key}Description` as keyof typeof HealthPlansEnum];
                                if (!description) {
                                    return null;
                                }

                                return (
                                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={plan}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={digitalSignatureForm.HealthPlans.includes(plan)}
                                                    onChange={() => handleHealthPlanChange(plan)}
                                                />
                                            }
                                            label={<Typography>{description}</Typography>}
                                        />
                                    </Grid>
                                );
                            })}
                        {digitalSignatureForm.HealthPlans.includes(HealthPlansEnum.Other) && (
                            <Grid size={12}>
                                <TextField
                                    sx={{ width: '100%' }}
                                    label="Nombre del plan"
                                    variant="standard"
                                    value={digitalSignatureForm.HealthPlanOther}
                                    onChange={(e) => {
                                        dispatch(
                                            setDigitalSignatureForm({
                                                ...digitalSignatureForm,
                                                HealthPlanOther: e.target.value,
                                            })
                                        );
                                    }}
                                />
                            </Grid>
                        )}
                    </Grid>
                </FormGroup>
                <Typography variant="body1">
                    Entiendo que cualquier información personal de salud o de otra índole liberada a la persona u organización identificada anteriormente podría
                    ser sujeta a divulgación adicional por dicha persona/organización y por tanto podría ya no estar protegida por las leyes de privacidad
                    federales y estatales que aplican. Esta autorización es válida desde la fecha de firma mía/de mi representante, presente abajo, y expira al
                    primero que suceda de{' '}
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <FormControl>
                        <DatePicker
                            label="Fecha de vencimiento"
                            slotProps={{ textField: { sx: { width: '100%' } } }}
                            onChange={(date) =>
                                dispatch(
                                    setDigitalSignatureForm({
                                        ...digitalSignatureForm,
                                        ConsentExpiration: date,
                                    })
                                )
                            }
                            value={digitalSignatureForm.ConsentExpiration ? (typeof digitalSignatureForm.ConsentExpiration === 'string' ? new Date(digitalSignatureForm.ConsentExpiration) : digitalSignatureForm.ConsentExpiration) : null}
                        />
                    </FormControl>
                </LocalizationProvider>
                <Typography variant="body1" pt={2}>
                    Entiendo que tengo el derecho a revocar esta autorización proveyendo un aviso por escrito. Sin embargo, esta autorización no puede ser
                    revocada si empleados o agentes ya han actuado con base en la misma antes de haber recibido mi aviso escrito. Además, entiendo que tengo del
                    derecho a tener una copia de esta autorización.
                </Typography>
                <Typography variant="body1">
                    Adicionalmente, entiendo que esta autorización es voluntaria y que puedo rehusarme a firmarla. El rehusarme a firmar no afectará mi
                    elegibilidad para recibir beneficios o inscripciones o pagos o cobertura de servicios.
                </Typography>
                <Typography variant="h6" fontWeight={'bold'} id="health-info-signature">
                    HE LEÍDO O SE ME HA LEÍDO ESTE CONSENTIMIENTO Y LO ENTIENDO COMPLETAMENTE; HE TENIDO LA OPORTUNIDAD DE HACER PREGUNTAS Y MIS PREGUNTAS HAN
                    SIDO RESPONDIDAS.
                </Typography>
            </Stack>
            <SignatureTypeTabs isSpanish={true} />
            <Divider />
            <Box p={2} textAlign={'left'}>
                {digitalSignatureForm.SignatureType === ConsentSignatureTypeEnum.Patient && (
                    <>
                        <Stack spacing={2} direction={'row'} pb={2}>
                            <TextField
                                id="member-name"
                                error={digitalSignatureForm.MemberName.length === 0}
                                variant="standard"
                                value={digitalSignatureForm.MemberName}
                                label={`Nombre del paciente`}
                                fullWidth
                                onChange={(e) =>
                                    dispatch(
                                        setDigitalSignatureForm({
                                            ...digitalSignatureForm,
                                            MemberName: e.target.value,
                                        })
                                    )
                                }
                            />
                            <TextField variant="standard" value={`${formatDate(new Date(), 'MM-dd-yyyy')}`} label={`Fecha`} fullWidth />
                        </Stack>
                        <Signature
                            isSpanish={true}
                            buttonLabel={'Firma del paciente'}
                            isOpen={openReleaseHealthInfoSignature}
                            setIsOpen={setOpenReleaseHealthInfoSignature}
                            signature={digitalSignatureForm.MemberSignatureTwo}
                            setSignature={(signature: string) =>
                                dispatch(
                                    setDigitalSignatureForm({
                                        ...digitalSignatureForm,
                                        MemberSignatureTwo: signature,
                                    })
                                )
                            }
                        />
                    </>
                )}
                {digitalSignatureForm.SignatureType === ConsentSignatureTypeEnum.GuardianOrCaregiver && (
                    <GuardianOrCaregiverInput
                        isSpanish={true}
                        openGuardianOrCaregiver={openGuardianOrCareGiverHealthInfo}
                        setOpenGuardianOrCaregiver={setOpenGuardianOrCareGiverHealthInfo}
                        guardianOrCareGiver={digitalSignatureForm.GuardianOrCaregiverSignatureHealthInfo}
                        setGuardianOrCareGiver={(signature: string) =>
                            dispatch(
                                setDigitalSignatureForm({
                                    ...digitalSignatureForm,
                                    GuardianOrCaregiverSignatureHealthInfo: signature,
                                })
                            )
                        }
                        openGuardianOrCareGiverWitness={openGuardianOrCareGiverWitnessHealthInfo}
                        setOpenGuardianOrCareGiverWitness={setOpenGuardianOrCareGiverWitnessHealthInfo}
                        guardianOrCareGiverWitness={digitalSignatureForm.WitnessHealthInfo}
                        setGuardianOrCareGiverWitness={(signature: string) =>
                            dispatch(
                                setDigitalSignatureForm({
                                    ...digitalSignatureForm,
                                    WitnessHealthInfo: signature,
                                })
                            )
                        }
                    />
                )}
                {digitalSignatureForm.SignatureType === ConsentSignatureTypeEnum.Verbal && (
                    <>
                        {/* Verbal Consent */}
                        <Stack>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={digitalSignatureForm.VerbalConsent}
                                        onChange={() =>
                                            dispatch(
                                                setDigitalSignatureForm({
                                                    ...digitalSignatureForm,
                                                    VerbalConsent: !digitalSignatureForm.VerbalConsent,
                                                })
                                            )
                                        }
                                    />
                                }
                                label={
                                    <Typography>
                                        El consentimiento verbal es necesario si el miembro o representante legal no puede firmar físicamente en este momento y
                                        sin embargo se ha acordado explícitamente el consentimiento para la atención.
                                        <b> El consentimiento verbal debe ser presenciado por dos personas por teléfono o en persona.</b>
                                    </Typography>
                                }
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={digitalSignatureForm.ReviewedTwo}
                                        onChange={() =>
                                            dispatch(
                                                setDigitalSignatureForm({
                                                    ...digitalSignatureForm,
                                                    ReviewedTwo: !digitalSignatureForm.ReviewedTwo,
                                                })
                                            )
                                        }
                                    />
                                }
                                label={
                                    <Typography>
                                        <b> El proveedor ha revisado esta página del formulario de consentimiento con el miembro</b>
                                    </Typography>
                                }
                            />
                            <Grid container spacing={2}>
                                <Grid size={6}>
                                    <TextField
                                        variant="outlined"
                                        required
                                        fullWidth
                                        helperText="Firma Digital del Testigo"
                                        sx={{ mt: 5 }}
                                        label={'Testigo Uno'}
                                        value={digitalSignatureForm.VerbalConsentWitnessOne}
                                        onChange={(e) =>
                                            dispatch(
                                                setDigitalSignatureForm({
                                                    ...digitalSignatureForm,
                                                    VerbalConsentWitnessOne: e.target.value,
                                                })
                                            )
                                        }
                                    />
                                </Grid>
                                <Grid size={6}>
                                    <TextField
                                        variant="outlined"
                                        required
                                        fullWidth
                                        helperText="Firma Digital del Testigo"
                                        sx={{ mt: 5 }}
                                        label={'Testigo Dos'}
                                        value={digitalSignatureForm.VerbalConsentWitnessTwo}
                                        onChange={(e) =>
                                            dispatch(
                                                setDigitalSignatureForm({
                                                    ...digitalSignatureForm,
                                                    VerbalConsentWitnessTwo: e.target.value,
                                                })
                                            )
                                        }
                                    />
                                </Grid>
                                <Grid size={12}>
                                    <Signature
                                        isSpanish={true}
                                        buttonLabel={'Firma del Testigo'}
                                        isOpen={openVerbalConsent}
                                        setIsOpen={setOpenVerbalConsent}
                                        signature={digitalSignatureForm.VerbalConsentSignature}
                                        setSignature={(signature: string) =>
                                            dispatch(
                                                setDigitalSignatureForm({
                                                    ...digitalSignatureForm,
                                                    VerbalConsentSignature: signature,
                                                })
                                            )
                                        }
                                    />
                                </Grid>
                            </Grid>
                        </Stack>
                    </>
                )}
            </Box>
        </>
    );
}
